import React, { useState, useEffect, useCallback } from 'react';
import { doc, getDoc, collection, getDocs, collectionGroup, query, where } from 'firebase/firestore';
import { db } from '../firebase';
import { classInfoService } from '../services/ClassInfoService';
import './StudentHomeView.css';

function StudentHomeView({ student, schoolId, onJoinClass }) {
  const [schoolClass, setSchoolClass] = useState(null);
  const [school, setSchool] = useState(null);
  const [onlineClasses, setOnlineClasses] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [grades, setGrades] = useState([]);
  const [timetable, setTimetable] = useState([]);
  const [loading, setLoading] = useState(true);

  const loadHomeData = useCallback(async () => {
    try {
      setLoading(true);

      if (student.classId) {
        const classData = await classInfoService.getClassInfo(student.classId);
        setSchoolClass(classData);

        // Load school info using schoolId from props or classData
        const effectiveSchoolId = schoolId || classData.schoolId;
        if (effectiveSchoolId) {
          const schoolDoc = await getDoc(doc(db, 'schools', effectiveSchoolId));
          if (schoolDoc.exists()) {
            setSchool(schoolDoc.data());
          }
        }

        const onlineClassesRef = collection(db, 'classes', student.classId, 'onlineClasses');
        const onlineClassesSnapshot = await getDocs(onlineClassesRef);
        const classes = onlineClassesSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
        }));
        setOnlineClasses(classes);

        // Timetables live in the top-level `timetables` collection, one doc
        // per class with an `entries` array — NOT classes/{id}/timetable.
        const timetableDoc = await getDoc(doc(db, 'timetables', student.classId));
        const timetableData = timetableDoc.exists()
          ? (timetableDoc.data().entries || [])
          : [];
        setTimetable(timetableData);

        const assignmentsRef = collection(db, 'classes', student.classId, 'assignments');
        const assignmentsSnapshot = await getDocs(assignmentsRef);
        const assignmentsData = assignmentsSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
        }));
        setAssignments(assignmentsData);

        // Grades live at classes/{c}/assignments/{a}/grades/{g} (iOS) and the
        // top-level `grades` collection (batch writes) — collectionGroup covers both.
        const ids = [student.id, student.studentId].filter(Boolean);
        const gradesSnapshot = await getDocs(query(
          collectionGroup(db, 'grades'),
          where('studentId', 'in', ids.slice(0, 30))
        ));
        const gradesData = gradesSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
        }));
        setGrades(gradesData);
      }
    } catch (error) {
      console.error('Error loading home data:', error);
    } finally {
      setLoading(false);
    }
  }, [student.classId, student.id, student.studentId, schoolId]);

  useEffect(() => {
    loadHomeData();
  }, [loadHomeData]);

  const getCurrentClass = () => {
    const now = new Date();
    const currentDay = now.toLocaleDateString('en-US', { weekday: 'long' });
    const currentTime = now.getHours() * 60 + now.getMinutes();

    return timetable.find(entry => {
      const entryDay = entry.dayOfWeek || entry.day;
      if (entryDay !== currentDay) return false;
      const [startHours, startMins] = entry.startTime.split(':').map(Number);
      const [endHours, endMins] = entry.endTime.split(':').map(Number);
      const startMinutes = startHours * 60 + startMins;
      const endMinutes = endHours * 60 + endMins;
      return currentTime >= startMinutes && currentTime <= endMinutes;
    });
  };

  const getActiveOnlineClass = () => {
    const now = new Date();
    return onlineClasses.find(c => {
      const isActive = c.status === 'Active' || c.status === 'active';
      const classDate = new Date(c.date);
      const isToday = classDate.toDateString() === now.toDateString();
      return isActive && isToday;
    });
  };

  const getAssignmentsDueToday = () => {
    const today = new Date().toDateString();
    return assignments.filter(a => {
      const dueDate = new Date(a.dueDate);
      return dueDate.toDateString() === today;
    });
  };

  const getUpcomingAssignments = () => {
    const today = new Date();
    return assignments
      .filter(a => new Date(a.dueDate) > today)
      .sort((a, b) => new Date(a.dueDate) - new Date(b.dueDate))
      .slice(0, 3);
  };

  const getRecentGrades = () => {
    return grades
      .sort((a, b) => new Date(b.gradedAt) - new Date(a.gradedAt))
      .slice(0, 3);
  };

  const formatTime12Hour = (time24) => {
    const [hours, minutes] = time24.split(':').map(Number);
    const ampm = hours >= 12 ? 'PM' : 'AM';
    const hours12 = hours % 12 || 12;
    return `${hours12}:${minutes.toString().padStart(2, '0')} ${ampm}`;
  };

  if (loading) {
    return <div className="loading">Loading...</div>;
  }

  const currentClass = getCurrentClass();
  const activeOnlineClass = getActiveOnlineClass();
  const assignmentsDueToday = getAssignmentsDueToday();
  const upcomingAssignments = getUpcomingAssignments();
  const recentGrades = getRecentGrades();

  return (
    <div className="student-home-view">
      <div className="welcome-card">
        <h3>Hello, {student.firstName}!</h3>
        {schoolClass && <p className="class-name">{schoolClass.name}</p>}
        {school && <p className="school-name">{school.name}</p>}
      </div>

      <div className="dashboard-grid">
        {currentClass && (
          <div className="card current-class-card">
            <div className="card-header">
              <span className="card-icon">🕐</span>
              <h3>Current Class</h3>
            </div>
            <div className="class-info">
              <h4>{currentClass.subjectName}</h4>
              {currentClass.room && <p>Room: {currentClass.room}</p>}
              <p>{formatTime12Hour(currentClass.startTime)} - {formatTime12Hour(currentClass.endTime)}</p>
            </div>
          </div>
        )}

        {activeOnlineClass && (
          <div className="card online-class-card">
            <div className="card-header">
              <span className="card-icon">📹</span>
              <h3>Live Now</h3>
            </div>
            <div className="class-info">
              <h4>{activeOnlineClass.subjectName}</h4>
              <p>{activeOnlineClass.topic}</p>
              <button className="join-button" onClick={() => onJoinClass(activeOnlineClass)}>
                Join Class
              </button>
            </div>
          </div>
        )}

        <div className="card assignments-card">
          <div className="card-header">
            <span className="card-icon">📅</span>
            <h3>Due Today</h3>
            <span className="count-badge">{assignmentsDueToday.length}</span>
          </div>
          {assignmentsDueToday.length === 0 ? (
            <p className="no-items">No assignments due today</p>
          ) : (
            <div className="items-list">
              {assignmentsDueToday.slice(0, 3).map(assignment => (
                <div key={assignment.id} className="item">
                  <h4>{assignment.title}</h4>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="card upcoming-assignments-card">
          <div className="card-header">
            <span className="card-icon">📝</span>
            <h3>Upcoming</h3>
          </div>
          {upcomingAssignments.length === 0 ? (
            <p className="no-items">No upcoming assignments</p>
          ) : (
            <div className="items-list">
              {upcomingAssignments.map(assignment => (
                <div key={assignment.id} className="item">
                  <h4>{assignment.title}</h4>
                  <p>Due: {new Date(assignment.dueDate).toLocaleDateString()}</p>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="card grades-card">
          <div className="card-header">
            <span className="card-icon">📊</span>
            <h3>Recent Grades</h3>
          </div>
          {recentGrades.length === 0 ? (
            <p className="no-items">No recent grades</p>
          ) : (
            <div className="items-list">
              {recentGrades.map(grade => (
                <div key={grade.id} className="item">
                  <h4>{grade.subject || 'Subject'}</h4>
                  <p className="grade-value">{grade.score || grade.grade}</p>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default StudentHomeView;
